import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  FormLabel, RadioGroup, FormControlLabel, Radio,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createStrategicIssue,
  createStrategicProcess,
  fetchValueChains,
  fetchBusinessProcesses,
  fetchFinancialGoals,
  fetchCustomerValues,
  fetchStrategicGoals,
  fetchStrategicIssues,
  fetchStrategicProcesses,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmStrategicIssueCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [kpiType, setKpiType] = useState("valueChain"); // "valueChain" | "businessProcess"
  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");

  // 結果目標モード: バリューチェーン KPI
  const [selectedVcIds, setSelectedVcIds] = useState([]);
  const [vcSelectedIndicatorNames, setVcSelectedIndicatorNames] = useState([]);
  const [valueChains, setValueChains] = useState([]);

  // 原因目標モード: 子ビジネスプロセス KPI
  const [selectedBPIds, setSelectedBPIds] = useState([]);
  const [bpSelectedIndicatorNames, setBpSelectedIndicatorNames] = useState([]);
  const [childBusinessProcesses, setChildBusinessProcesses] = useState([]);

  // ソース（モードで切り替え）
  const [sourceIds, setSourceIds] = useState([]);

  // ソース用データ
  const [financialGoals, setFinancialGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [strategicProcesses, setStrategicProcesses] = useState([]);

  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());

  // 子BP → 親バリューチェーン名マップ（原因目標モードの表示用）
  const parentVcMap = useMemo(() => {
    const map = {};
    valueChains.forEach((vc) => {
      (vc.associatedBusinessProcesses || []).forEach((bp) => {
        map[bp.businessProcessId] = vc.businessProcessName;
      });
    });
    return map;
  }, [valueChains]);

  // 選択VC の indicators（結果目標モード）
  const selectedVcs = useMemo(
    () => selectedVcIds.map((id) => valueChains.find((vc) => vc.businessProcessId === id)).filter(Boolean),
    [selectedVcIds, valueChains]
  );
  const vcSourceIndicators = useMemo(() => {
    const all = selectedVcs.flatMap((vc) =>
      (vc.indicators || []).map((ind) => ({ ...ind, _vcName: vc.businessProcessName }))
    );
    const seen = new Set();
    return all.filter((ind) => {
      if (seen.has(ind.indicatorName)) return false;
      seen.add(ind.indicatorName);
      return true;
    });
  }, [selectedVcs]);

  // 選択BP の indicators（原因目標モード）
  const selectedBPs = useMemo(
    () => selectedBPIds.map((id) => childBusinessProcesses.find((bp) => bp.businessProcessId === id)).filter(Boolean),
    [selectedBPIds, childBusinessProcesses]
  );
  const bpSourceIndicators = useMemo(() => {
    const all = selectedBPs.flatMap((bp) => {
      const vcName = parentVcMap[bp.businessProcessId];
      return (bp.indicators || []).map((ind) => ({
        ...ind,
        _bpName: bp.businessProcessName,
        _parentVcName: vcName || null,
      }));
    });
    const seen = new Set();
    return all.filter((ind) => {
      if (seen.has(ind.indicatorName)) return false;
      seen.add(ind.indicatorName);
      return true;
    });
  }, [selectedBPs, parentVcMap]);

  useEffect(() => {
    (async () => {
      try {
        const [vcs, bps, fgoals, cvals, sgoals, sissues, sprocs] = await Promise.all([
          fetchValueChains(businessDomainId).catch(() => []),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchStrategicProcesses(businessDomainId).catch(() => []),
        ]);
        setValueChains(Array.isArray(vcs) ? vcs : []);
        setChildBusinessProcesses(Array.isArray(bps) ? bps : []);
        setFinancialGoals(Array.isArray(fgoals) ? fgoals : []);
        setCustomerValues(Array.isArray(cvals) ? cvals : []);
        setStrategicGoals(Array.isArray(sgoals) ? sgoals : []);
        setStrategicIssues(Array.isArray(sissues) ? sissues : []);
        setStrategicProcesses(Array.isArray(sprocs) ? sprocs : []);
        const used = new Set([
          ...(Array.isArray(sissues) ? sissues : []).flatMap((sf) => sf.indicators || []).map((i) => i.indicatorName),
          ...(Array.isArray(sprocs) ? sprocs : []).flatMap((sf) => sf.indicators || []).map((i) => i.indicatorName),
        ].filter(Boolean));
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleKpiTypeChange = (e) => {
    setKpiType(e.target.value);
    setSelectedVcIds([]);
    setSelectedBPIds([]);
    setSourceIds([]);
    setVcSelectedIndicatorNames([]);
    setBpSelectedIndicatorNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (kpiType === "valueChain") {
        if (vcSourceIndicators.length > 0 && vcSelectedIndicatorNames.length === 0) {
          notify.error(t("financialGoal.indicatorRequired"));
          return;
        }
        const allSources = [...financialGoals, ...customerValues, ...strategicGoals];
        const sources = sourceIds
          .map((id) => allSources.find((s) => s.successFactorId === id))
          .filter(Boolean)
          .map((s) => ({ successFactorId: s.successFactorId, successFactorName: s.successFactorName }));
        const indicators = vcSelectedIndicatorNames
          .map((name) => vcSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        const bpsToSend = selectedVcs.map((vc) => ({
          businessProcessId: vc.businessProcessId,
          businessProcessName: vc.businessProcessName,
        }));
        await createStrategicIssue({
          successFactorName,
          successFactorDescription,
          businessProcesses: bpsToSend,
          indicators,
          sources,
          businessDomainId,
          companyId,
        });
      } else {
        if (bpSourceIndicators.length > 0 && bpSelectedIndicatorNames.length === 0) {
          notify.error(t("financialGoal.indicatorRequired"));
          return;
        }
        const allSources = [...strategicProcesses, ...strategicIssues];
        const sources = sourceIds
          .map((id) => allSources.find((s) => s.successFactorId === id))
          .filter(Boolean)
          .map((s) => ({ successFactorId: s.successFactorId, successFactorName: s.successFactorName }));
        const indicators = bpSelectedIndicatorNames
          .map((name) => bpSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        const bpsToSend = selectedBPs.map((bp) => ({
          businessProcessId: bp.businessProcessId,
          businessProcessName: bp.businessProcessName,
        }));
        await createStrategicProcess({
          successFactorName,
          successFactorDescription,
          businessProcesses: bpsToSend,
          indicators,
          sources,
          businessDomainId,
          companyId,
        });
      }
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/strategic-issues`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("strategicIssue.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* ラジオボタン */}
          <Grid size={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t("strategicIssue.title")}</FormLabel>
              <RadioGroup row value={kpiType} onChange={handleKpiTypeChange}>
                <FormControlLabel value="valueChain" control={<Radio />} label="結果目標" />
                <FormControlLabel value="businessProcess" control={<Radio />} label="原因目標" />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* 名前 */}
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          {/* 説明 */}
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {/* バリューチェーン選択（結果目標モード） */}
          {kpiType === "valueChain" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("valueChain.list.title")}</InputLabel>
                <Select
                  multiple
                  value={selectedVcIds}
                  onChange={(e) => { setSelectedVcIds(e.target.value); setVcSelectedIndicatorNames([]); }}
                  input={<OutlinedInput label={t("valueChain.list.title")} />}
                >
                  {valueChains.map((vc) => (
                    <MenuItem key={vc.businessProcessId} value={vc.businessProcessId}>
                      {vc.businessProcessName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* 子ビジネスプロセス選択（原因目標モード：1件のみ） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("businessProcess.title")}</InputLabel>
                <Select
                  value={selectedBPIds[0] || ""}
                  onChange={(e) => { setSelectedBPIds(e.target.value ? [e.target.value] : []); setBpSelectedIndicatorNames([]); }}
                  label={t("businessProcess.title")}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {childBusinessProcesses.map((bp) => {
                    const vcName = parentVcMap[bp.businessProcessId];
                    return (
                      <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                        {vcName ? `${vcName} > ${bp.businessProcessName}` : bp.businessProcessName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ソース選択（結果目標モード）: 財務目標 + 顧客価値 + 製品価値 */}
          {kpiType === "valueChain" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("strategicIssue.sources.label")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={(e) => setSourceIds(e.target.value)}
                  input={<OutlinedInput label={t("strategicIssue.sources.label")} />}
                >
                  {financialGoals.length > 0 && [
                    <MenuItem key="__fg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("financialGoal.list.title")} ──
                    </MenuItem>,
                    ...financialGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {customerValues.length > 0 && [
                    <MenuItem key="__cv_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("customerValue.list.title")} ──
                    </MenuItem>,
                    ...customerValues.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {strategicGoals.length > 0 && [
                    <MenuItem key="__sg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("strategicGoal.list.title")} ──
                    </MenuItem>,
                    ...strategicGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ソース選択（原因目標モード：1件のみ）: 戦略目標（結果）+ 戦略目標（原因）自己参照 */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("strategicIssue.sources.label")}</InputLabel>
                <Select
                  value={sourceIds[0] || ""}
                  onChange={(e) => setSourceIds(e.target.value ? [e.target.value] : [])}
                  label={t("strategicIssue.sources.label")}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {strategicIssues.length > 0 && [
                    <MenuItem key="__si_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("strategicIssue.list.title")}（結果） ──
                    </MenuItem>,
                    ...strategicIssues.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {strategicProcesses.length > 0 && [
                    <MenuItem key="__sp_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("strategicIssue.list.title")}（原因） ──
                    </MenuItem>,
                    ...strategicProcesses.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* KPI 指標選択（結果目標モード） */}
          {kpiType === "valueChain" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  disabled={vcSourceIndicators.length === 0}
                  value={vcSelectedIndicatorNames}
                  onChange={(e) => setVcSelectedIndicatorNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {vcSourceIndicators.map((ind) => {
                    const isUsed = usedIndicatorNames.has(ind.indicatorName);
                    return (
                      <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={isUsed}>
                        [{ind._vcName}] {ind.indicatorName}（{ind.unit}）{isUsed ? " （登録済み）" : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* KPI 指標選択（原因目標モード） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  disabled={bpSourceIndicators.length === 0}
                  value={bpSelectedIndicatorNames}
                  onChange={(e) => setBpSelectedIndicatorNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {bpSourceIndicators.map((ind) => {
                    const prefix = ind._parentVcName
                      ? `[${ind._parentVcName} > ${ind._bpName}] `
                      : `[${ind._bpName}] `;
                    const isUsed = usedIndicatorNames.has(ind.indicatorName);
                    return (
                      <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={isUsed}>
                        {prefix}{ind.indicatorName}（{ind.unit}）{isUsed ? " （登録済み）" : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}

        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/strategic-issues`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
